import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Grid,
  Chip,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { LineChart as MuiLineChart, PieChart } from '@mui/x-charts';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { analyticsApi } from '../../api/analytics';
import { QuoteConversionMetrics } from '../../types/analytics.types';

interface QuoteConversionMetricsProps {
  loading?: boolean;
}

export const QuoteConversionMetricsComponent: React.FC<QuoteConversionMetricsProps> = ({ 
  loading: externalLoading = false 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [data, setData] = useState<QuoteConversionMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await analyticsApi.getQuoteConversionMetrics();
        setData(response.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch quote conversion metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading || externalLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  // Prepare chart data
  const monthlyTrendsData = data.monthlyTrends.map(trend => ({
    month: trend.month,
    conversionTime: trend.averageConversionTime,
    opportunitiesCreated: trend.opportunitiesCreated,
    opportunitiesCompleted: trend.opportunitiesCompleted,
    conversionRate: trend.conversionRate,
  }));

  const stageDistributionData = [
    { label: 'Draft', value: data.stageDistribution.draft, color: '#f44336' },
    { label: 'Issued', value: data.stageDistribution.issued, color: '#ff9800' },
    { label: 'Selected', value: data.stageDistribution.selected, color: '#2196f3' },
    { label: 'Completed', value: data.stageDistribution.completed, color: '#4caf50' },
    { label: 'Rejected', value: data.stageDistribution.rejected, color: '#9e9e9e' },
  ];

  const conversionRatesData = [
    { stage: 'Draft → Issued', rate: data.conversionRates.draftToIssued },
    { stage: 'Issued → Selected', rate: data.conversionRates.issuedToSelected },
    { stage: 'Selected → Completed', rate: data.conversionRates.selectedToCompleted },
  ];

  return (
    <Grid container spacing={3}>
      {/* Key Metrics Summary */}
      <Grid item xs={12}>
        <Card sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          color: 'white'
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Quote Conversion Timescale Metrics
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {data.totalOpportunities} opportunities · {data.versionEvents.totalVersions} versions ·{' '}
              {data.versionEvents.issuedVersions} issued artefacts
              {data.versionEvents.legacyDownloadedVersions > 0 &&
                ` (incl. ${data.versionEvents.legacyDownloadedVersions} legacy downloads)`}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="primary">
                    {data.totalAverageConversionTime}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg. Total Conversion (days)
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="success.main">
                    {data.conversionRates.overallConversion}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Overall Conversion Rate
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="info.main">
                    {data.averageTimeToIssued}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg. Time to Issue (days)
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="warning.main">
                    {data.averageTimeToSelected}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg. Time to Select (days)
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Commission Uplift Metrics */}
      <Grid item xs={12}>
        <Card sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          color: 'white'
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Commission Uplift Analysis
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="success.main">
                    {data.averageCommissionUplift?.toFixed(2) || '0.00'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Average Commission Uplift
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="info.main">
                    {data.opportunitiesWithUplift || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Opportunities with Uplift
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="warning.main">
                    {data.minCommissionUplift?.toFixed(2) || '0.00'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Min Uplift
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="error.main">
                    {data.maxCommissionUplift?.toFixed(2) || '0.00'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Max Uplift
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Monthly Trends Chart */}
      <Grid item xs={12} lg={8}>
        <Card sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          color: 'white'
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Monthly Conversion Trends
            </Typography>
            <Box sx={{ height: 350 }}>
              <MuiLineChart
                series={[
                  {
                    data: monthlyTrendsData.map(d => d.conversionTime),
                    label: 'Avg. Conversion Time (days)',
                    color: '#2196f3',
                  },
                  {
                    data: monthlyTrendsData.map(d => d.conversionRate),
                    label: 'Conversion Rate (%)',
                    color: '#4caf50',
                  },
                ]}
                xAxis={[
                  {
                    data: monthlyTrendsData.map(d => d.month),
                    scaleType: 'point',
                  },
                ]}
                height={350}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Conversion Rates Bar Chart */}
      <Grid item xs={12} lg={4}>
        <Card sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          color: 'white'
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Stage Conversion Rates
            </Typography>
            <Box sx={{ height: isMobile ? 350 : 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={conversionRatesData}
                  margin={{ 
                    top: 20, 
                    right: isMobile ? 10 : 30, 
                    left: isMobile ? 10 : 20, 
                    bottom: isMobile ? 100 : 80 
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis 
                    dataKey="stage"
                    tick={{ fill: '#fff', fontSize: isMobile ? 9 : 11 }}
                    stroke="rgba(255, 255, 255, 0.2)"
                    angle={isMobile ? -45 : -25}
                    textAnchor="end"
                    height={isMobile ? 100 : 80}
                  />
                  <YAxis 
                    tick={{ fill: '#fff', fontSize: isMobile ? 10 : 12 }}
                    stroke="rgba(255, 255, 255, 0.2)"
                    label={{ value: '%', angle: -90, position: 'insideLeft', fill: '#fff' }}
                  />
                  <Tooltip 
                    contentStyle={{
                      background: 'rgba(30, 41, 59, 0.95)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      color: 'rgba(255, 255, 255, 0.9)',
                    }}
                    formatter={(value: number) => [`${value.toFixed(1)}%`]}
                    labelStyle={{ color: '#fff', fontWeight: 'bold' }}
                    cursor={false}
                  />
                  <Bar dataKey="rate" fill={theme.palette.primary.main} radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Stage Distribution Pie Chart */}
      <Grid item xs={12} md={6}>
        <Card sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          color: 'white'
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Quote Stage Distribution
            </Typography>
            <Box sx={{ height: 300 }}>
              <PieChart
                series={[
                  {
                    data: stageDistributionData,
                    highlightScope: { faded: 'global', highlighted: 'item' },
                  },
                ]}
                height={300}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Monthly Quote Volume */}
      <Grid item xs={12} md={6}>
        <Card sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          color: 'white'
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Monthly Opportunity Volume
            </Typography>
            <Box sx={{ height: isMobile ? 300 : 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={monthlyTrendsData}
                  margin={{ 
                    top: 20, 
                    right: isMobile ? 10 : 30, 
                    left: isMobile ? 10 : 20, 
                    bottom: isMobile ? 80 : 60 
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis 
                    dataKey="month"
                    tick={{ fill: '#fff', fontSize: isMobile ? 9 : 11 }}
                    stroke="rgba(255, 255, 255, 0.2)"
                    angle={isMobile ? -35 : -15}
                    textAnchor="end"
                    height={isMobile ? 80 : 60}
                  />
                  <YAxis 
                    tick={{ fill: '#fff', fontSize: isMobile ? 10 : 12 }}
                    stroke="rgba(255, 255, 255, 0.2)"
                  />
                  <Tooltip 
                    contentStyle={{
                      background: 'rgba(30, 41, 59, 0.95)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      color: 'rgba(255, 255, 255, 0.9)',
                    }}
                    labelStyle={{ color: '#fff', fontWeight: 'bold' }}
                    cursor={false}
                  />
                  <Legend 
                    wrapperStyle={{ color: '#fff' }}
                    iconType="circle"
                  />
                  <Bar dataKey="opportunitiesCreated" name="Opportunities Created" fill={theme.palette.secondary.main} radius={[8, 8, 0, 0]} />
                  <Bar dataKey="opportunitiesCompleted" name="Opportunities Completed" fill="#4caf50" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Detailed Timing Breakdown */}
      <Grid item xs={12}>
        <Card sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          color: 'white'
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Average Time Breakdown by Stage
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center" p={2}>
                  <Chip 
                    label={`${data.averageTimeToIssued} days`} 
                    color="info" 
                    size="medium" 
                    sx={{ mb: 1, fontSize: '1.1rem', height: '36px' }}
                  />
                  <Typography variant="body2">
                    Draft → Issued
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center" p={2}>
                  <Chip 
                    label={`${data.averageTimeToSelected} days`} 
                    color="warning" 
                    size="medium" 
                    sx={{ mb: 1, fontSize: '1.1rem', height: '36px' }}
                  />
                  <Typography variant="body2">
                    Issued → Selected
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center" p={2}>
                  <Chip 
                    label={`${data.averageTimeToCompleted} days`} 
                    color="success" 
                    size="medium" 
                    sx={{ mb: 1, fontSize: '1.1rem', height: '36px' }}
                  />
                  <Typography variant="body2">
                    Selected → Completed
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};
